import { AfterContentInit, AfterViewInit, ElementRef, Renderer2, OnDestroy } from '@angular/core';
import { Component, ContentChild, EventEmitter, Input, Output } from '@angular/core';
import { NgControl } from '@angular/forms';

import { BsDatepickerDirective } from 'ngx-bootstrap/datepicker';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { TranslateService } from '@ngx-translate/core';
import { defineLocale, enGbLocale, esLocale } from 'ngx-bootstrap/chronos';

@Component({
    selector: 'nh-datepicker',
    templateUrl: './datepicker.component.html',
    standalone: false
})
export class DatepickerComponent implements AfterContentInit, AfterViewInit, OnDestroy {
  @Input() readOnly = false;
  @ContentChild(BsDatepickerDirective, { static: false })
  dp: BsDatepickerDirective;
  @ContentChild(NgControl, { static: false })
  field: NgControl;
  @Output() close = new EventEmitter<any>();
  defaultLanguage;

  private focusTrapListeners: (() => void)[] = [];

  constructor(
    public localeService: BsLocaleService, 
    public translateService: TranslateService,
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) {
    defineLocale('es', esLocale);
    defineLocale('en', enGbLocale);
  }

  ngAfterContentInit() {
    this.defaultLanguage = this.translateService.getDefaultLang();
    this.languageCheck(this.defaultLanguage);

    this.translateService.onDefaultLangChange.subscribe((res) => this.languageCheck(res.lang));
    
    // Set up datepicker event handlers for accessibility
    if (this.dp) {
      this.dp.onShown?.subscribe(() => this.onDatepickerShown());
      this.dp.onHidden?.subscribe(() => this.onDatepickerHidden());
    }
  }

  ngAfterViewInit() {
    this.setupFocusTrap();
  }

  ngOnDestroy() {
    // Clean up all event listeners
    this.focusTrapListeners.forEach(unsubscribe => unsubscribe());
  }

  private setupFocusTrap() {
    // Trap Tab key when datepicker is open
    const tabListener = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Tab' && this.dp && this.dp.isOpen) {
        event.preventDefault();
        event.stopPropagation();
        this.handleTabInCalendar(event);
      }
    });
    this.focusTrapListeners.push(tabListener);

    // Handle Enter and Space keys for selecting dates/months/years
    const enterSpaceListener = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
      if ((event.key === 'Enter' || event.key === ' ') && this.dp && this.dp.isOpen) {
        const target = event.target as HTMLElement;
        const calendar = document.querySelector('bs-datepicker-container');
        
        if (calendar && calendar.contains(target) && target.tagName === 'TD' && !target.classList.contains('disabled') && !target.classList.contains('is-other-month')) {
          event.preventDefault();
          event.stopPropagation();
          // Try multiple methods to trigger selection
          
          // Method 0: Find the actual clickable element inside the TD
          const clickableElements = [
            target.querySelector('span[data-date]'),
            target.querySelector('span'),
            target.querySelector('[data-date]'),
            target.querySelector('.ng-star-inserted'),
            target.firstElementChild
          ].filter(Boolean);
          
          
          
          // Method 1: Try clicking child elements first
          let selectionSuccess = false;
          for (let i = 0; i < clickableElements.length && !selectionSuccess; i++) {
            const clickableEl = clickableElements[i] as HTMLElement;
            try {
              clickableEl?.click();
              // Check if datepicker closed (indicates successful selection)
              setTimeout(() => {
                if (!this.dp?.isOpen) {
                  selectionSuccess = true;
                }
              }, 100);
            } catch (error) {
            }
          }
          
          // Method 2: Direct TD click (fallback)
          if (!selectionSuccess) {
            target.click();
          }
          
          // Method 3: Dispatch synthetic events as final fallback
          setTimeout(() => {
            if (!this.dp?.isOpen) {
              return;
            }
            
            
            // Try on child elements first
            const primaryClickTarget = clickableElements[0] as HTMLElement || target;
            
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window
            });
            
            const mousedownEvent = new MouseEvent('mousedown', {
              bubbles: true,
              cancelable: true,
              view: window
            });
            
            const mouseupEvent = new MouseEvent('mouseup', {
              bubbles: true,
              cancelable: true,
              view: window
            });
            
            primaryClickTarget.dispatchEvent(mousedownEvent);
            primaryClickTarget.dispatchEvent(clickEvent);
            primaryClickTarget.dispatchEvent(mouseupEvent);
            
            
            // Final check
            
            
          }, 10);
        }
          
        
      }
    });
    this.focusTrapListeners.push(enterSpaceListener);

    // Handle Arrow keys for comprehensive navigation (header + body)
    const arrowKeyListener = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key) && this.dp && this.dp.isOpen) {
        const target = event.target as HTMLElement;
        const calendar = document.querySelector('bs-datepicker-container');        
        if (calendar && calendar.contains(target)) {
          // Handle arrow navigation for any focusable element in calendar (header buttons or body cells)
          const isButton = target.tagName === 'BUTTON';
          const isCell = target.tagName === 'TD' && !target.classList.contains('disabled');
          
          
          if (isButton || isCell) {
            event.preventDefault();
            event.stopPropagation();
            
            this.handleArrowNavigation(event.key, target, calendar);
          } 
        }
      }
    });
    this.focusTrapListeners.push(arrowKeyListener);

    // Handle Escape key to close datepicker
    const escapeListener = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape' && this.dp && this.dp.isOpen) {
        event.preventDefault();
        event.stopPropagation();
        
        // Get the toggle button
        const toggleButton = this.elementRef.nativeElement.querySelector('.datepicker-toggle');
        
        // Temporarily change aria-label to include "collapsed" for the announcement
        if (toggleButton) {
          this.renderer.setAttribute(toggleButton, 'aria-label', 'Choose date collapsed');
        }
        
        this.dp.hide();
        
        // Return focus to the toggle button
        if (toggleButton) {
          setTimeout(() => {
            toggleButton.focus();
            // Reset aria-label back to normal after a short delay
            setTimeout(() => {
              this.renderer.setAttribute(toggleButton, 'aria-label', 'Choose date');
            }, 100);
          }, 50);
        }
      }
    });
    this.focusTrapListeners.push(escapeListener);

    // Monitor for focus leaving calendar with more aggressive checking
    const focusoutListener = this.renderer.listen('document', 'focusout', () => {
      if (this.dp && this.dp.isOpen) {
        setTimeout(() => {
          const calendar = document.querySelector('bs-datepicker-container');
          const focusedElement = document.activeElement;
          
          
          if (calendar && (!focusedElement || !calendar.contains(focusedElement as HTMLElement))) {
            // Focus escaped - bring it back to first focusable element
            this.focusFirstCalendarElement(calendar);
          }
        }, 10);
      }
    });
    this.focusTrapListeners.push(focusoutListener);

    // Additional safety net - monitor for any clicks or other interactions
    const clickListener = this.renderer.listen('document', 'click', (event: Event) => {
      if (this.dp && this.dp.isOpen) {
        const target = event.target as HTMLElement;
        const calendar = document.querySelector('bs-datepicker-container');
        
        // If click is outside calendar, ensure focus stays in calendar
        if (calendar && !calendar.contains(target)) {
          setTimeout(() => {
            const currentFocus = document.activeElement;
            if (!calendar.contains(currentFocus as HTMLElement)) {
              this.focusFirstCalendarElement(calendar);
            }
          }, 50);
        }
      }
    });
    this.focusTrapListeners.push(clickListener);
  }

  private handleTabInCalendar(event: KeyboardEvent) {
    const calendar = document.querySelector('bs-datepicker-container');
    if (!calendar) return;

    // Get all focusable elements and filter out disabled ones
    let focusableElements = this.getFocusableElements(calendar);
    focusableElements = focusableElements.filter(el => !this.isElementDisabled(el));
    
    if (focusableElements.length === 0) {
      this.dp.hide();
      const toggleButton = this.elementRef.nativeElement.querySelector('.datepicker-toggle');
      if (toggleButton) {
        setTimeout(() => toggleButton.focus(), 50);
      }
      return;
    }

    const currentlyFocused = document.activeElement as HTMLElement;
    const currentIndex = focusableElements.indexOf(currentlyFocused);

    
    

    let nextIndex: number;
    if (currentIndex === -1) {
      nextIndex = event.shiftKey ? focusableElements.length - 1 : 0;
    } else {
      if (event.shiftKey) {
        nextIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
      }
    }

    const targetElement = focusableElements[nextIndex];
    if (targetElement) {
      targetElement.focus();
      
      // Verify focus actually moved
      
    }
  }

  private handleArrowNavigation(key: string, currentElement: HTMLElement, calendar: Element): void {
   
    
    // Get all focusable elements in proper tab order (header buttons + body cells)
    let allFocusableElements = this.getFocusableElements(calendar);
    allFocusableElements = allFocusableElements.filter(el => !this.isElementDisabled(el));
    
    
    if (allFocusableElements.length === 0) {
      return;
    }
    
    // Find current element index in the full focusable list
    const currentIndex = allFocusableElements.indexOf(currentElement);
    
    if (currentIndex === -1) {
      return;
    }
    
    // Separate header buttons and body cells for different navigation logic
    const headerButtons = allFocusableElements.filter(el => el.closest('.bs-datepicker-head'));
    const bodyCells = allFocusableElements.filter(el => !el.closest('.bs-datepicker-head'));
    
    
    const isInHeader = currentElement.closest('.bs-datepicker-head') !== null;
    
    let nextElement: HTMLElement | null = null;
    
    if (isInHeader) {
      nextElement = this.handleHeaderNavigation(key, currentElement, headerButtons, bodyCells);
    } else {
      nextElement = this.handleBodyNavigation(key, currentElement, bodyCells, headerButtons);
    }
    
    // Focus the target element if found
    if (nextElement && !this.isElementDisabled(nextElement)) {
      nextElement.focus();
    } else {
    }
  }

  private handleHeaderNavigation(key: string, currentButton: HTMLElement, headerButtons: HTMLElement[], bodyCells: HTMLElement[]): HTMLElement | null {
    const currentIndex = headerButtons.indexOf(currentButton);
    
    switch (key) {
      case 'ArrowLeft':
        // Move to previous header button, wrap to last button
        return currentIndex > 0 ? headerButtons[currentIndex - 1] : headerButtons[headerButtons.length - 1];
        
      case 'ArrowRight':
        // Move to next header button, wrap to first button
        return currentIndex < headerButtons.length - 1 ? headerButtons[currentIndex + 1] : headerButtons[0];
        
      case 'ArrowDown':
        // Move to first available body cell
        return bodyCells.length > 0 ? bodyCells[0] : null;
        
      case 'ArrowUp':
        // Stay in header - wrap around to last header button
        return currentIndex > 0 ? headerButtons[currentIndex - 1] : headerButtons[headerButtons.length - 1];
        
      default:
        return null;
    }
  }

  private handleBodyNavigation(key: string, currentCell: HTMLElement, bodyCells: HTMLElement[], headerButtons: HTMLElement[]): HTMLElement | null {
    const currentIndex = bodyCells.indexOf(currentCell);
    if (currentIndex === -1) return null;
    
    // Detect what kind of calendar view we're in to adjust grid dimensions
    const calendarTable = currentCell.closest('table');
    if (!calendarTable) return null;
    
    // Get actual row count and column count from the table
    const rows = Array.from(calendarTable.querySelectorAll('tbody tr, tr')).filter(row => {
      // Filter out header rows (day of week headers)
      const firstCell = row.querySelector('td');
      return firstCell && !firstCell.closest('thead');
    });
    
    let columnsPerRow = 7; // Default for date view
    if (rows.length > 0) {
      const firstRow = rows[0];
      const cellsInRow = firstRow.querySelectorAll('td').length;
      if (cellsInRow > 0) {
        columnsPerRow = cellsInRow;
      }
    }
    
    // Special handling for year view (typically 4 columns) and month view (typically 3-4 columns)
    const isYearView = currentCell.closest('.bs-years-calendar-view') !== null;
    const isMonthView = currentCell.closest('.bs-months-calendar-view') !== null;
    
    if (isYearView) {
      columnsPerRow = 4; // Year views typically use 4 columns
    } else if (isMonthView) {
      columnsPerRow = 3; // Month views typically use 3 columns
    }
    
    
    switch (key) {
      case 'ArrowLeft':
        // Move to previous cell, wrap to last cell
        return currentIndex > 0 ? bodyCells[currentIndex - 1] : bodyCells[bodyCells.length - 1];
        
      case 'ArrowRight':
        // Move to next cell, wrap to first cell
        return currentIndex < bodyCells.length - 1 ? bodyCells[currentIndex + 1] : bodyCells[0];
        
      case 'ArrowUp':
        // SIMPLIFIED LOGIC: Check if we're in first row by trying to find the previous row cell
        const upIndex = currentIndex - columnsPerRow;
        
        // If upIndex is valid and points to an existing cell, move there
        if (upIndex >= 0 && bodyCells[upIndex]) {
          return bodyCells[upIndex];
        } 
        
        // FALLBACK: If headerButtons array is empty, try to find them directly
        if (headerButtons.length === 0) {
          const directHeaderButtons = Array.from(currentCell.closest('bs-datepicker-container')?.querySelectorAll('.bs-datepicker-head button') || []) as HTMLElement[];          
          if (directHeaderButtons.length > 0) {
            const targetButton = directHeaderButtons[directHeaderButtons.length - 1];
            return targetButton;
          }
        } else {
          // Use the header buttons from the filtered list
          const targetButton = headerButtons[headerButtons.length - 1];
          return targetButton;
        }
        return null;
        
      case 'ArrowDown':
        // Try to move down one row
        const downIndex = currentIndex + columnsPerRow;
        if (downIndex < bodyCells.length) {
          return bodyCells[downIndex];
        } else {
          // Wrap to top row, same column
          const columnPosition = currentIndex % columnsPerRow;
          return bodyCells[columnPosition] || bodyCells[0];
        }
        
      default:
        return null;
    }
  }

  private getEmergencyFocusableElements(calendar: Element): HTMLElement[] {
    // Emergency fallback - get ANY visible element that might be focusable
    const allElements = Array.from(calendar.querySelectorAll('*')) as HTMLElement[];
    
    return allElements.filter(el => {
      const rect = el.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0;
      
      // Check if it's any kind of interactive element
      const isInteractive = el.tagName === 'BUTTON' || 
                           el.tagName === 'TD' || 
                           el.hasAttribute('tabindex') ||
                           el.hasAttribute('role') ||
                           el.onclick !== null;
      
      // Make sure it's not explicitly disabled
      const notDisabled = !el.hasAttribute('disabled') && 
                         !el.classList.contains('disabled');
      
      return isVisible && isInteractive && notDisabled;
    });
  }

  private getFocusableElements(calendar: Element): HTMLElement[] {
    // Get all interactive elements in the calendar, excluding disabled ones
    // IMPORTANT: Build proper tab order - header buttons first, then ALL body dates in order
    
    const elements: HTMLElement[] = [];
    
    // First, add header buttons (double-check they're not disabled)
    const headerButtons = Array.from(calendar.querySelectorAll('.bs-datepicker-head button:not([disabled]):not([style*="display: none"])')) as HTMLElement[];
    
    // Additional filter: ensure no disabled buttons slip through due to timing issues
    const enabledHeaderButtons = headerButtons.filter(button => {
      const isDisabled = button.hasAttribute('disabled') || 
                        button.getAttribute('aria-disabled') === 'true' ||
                        button.getAttribute('tabindex') === '-1';
      if (isDisabled) {
        return false;
      }
      return true;
    });
    
    elements.push(...enabledHeaderButtons);
    
    // Then, add ALL enabled date cells in row order (not just the first one)
    // RELAXED SELECTOR: Let's try finding any TD that could be a date cell
    let allDateCells = Array.from(calendar.querySelectorAll('td[tabindex="0"]:not(.disabled):not(.is-other-month):not([disabled]):not([aria-disabled="true"]):not([aria-hidden="true"])')) as HTMLElement[];
    
    // If no cells found with strict selector, try broader search
    if (allDateCells.length === 0) {
      allDateCells = Array.from(calendar.querySelectorAll('td:not(.disabled):not(.is-other-month)')) as HTMLElement[];
    }
    
   
    
    // Filter date cells to ensure they're in calendar body and properly enabled
    const validDateCells = allDateCells.filter(td => {
      // Make sure it's NOT in the header
      const isInHeader = td.closest('.bs-datepicker-head');
      if (isInHeader) {
        return false;
      }
      
      // Make sure it doesn't have disabled children
      const hasDisabledChild = td.querySelector('.disabled, [bsdatepickerdaydecorator].disabled');
      if (hasDisabledChild) {
        return false;
      }
      
      // Make sure it's in a calendar body container
      const calendarBody = td.closest('table, .bs-calendar-container, .bs-days-calendar-view, .bs-months-calendar-view, .bs-years-calendar-view');
      if (!calendarBody) {
        return false;
      }
      
      // Ensure valid date cells have proper tabindex (fix if missing)
      if (td.getAttribute('tabindex') !== '0') {
        td.setAttribute('tabindex', '0');
      }
      
      return true;
    });
    
    // Add all valid date cells to the elements array
    elements.push(...validDateCells);

    // Triple-check filter for visible and truly focusable elements
    const filteredElements = elements.filter(el => {
      // Check if element is truly visible and enabled
      const rect = el.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0 && el.offsetParent !== null;
      
      // Enhanced disabled checking - multiple ways dates can be disabled
      const isEnabled = !el.hasAttribute('disabled') && 
                       !el.classList.contains('disabled') &&
                       !el.hasAttribute('aria-disabled') &&
                       el.getAttribute('aria-disabled') !== 'true' &&
                       el.getAttribute('aria-hidden') !== 'true' &&
                       !el.classList.contains('is-other-month');
      
      // Additional check for buttons with pointer-events: none
      const style = window.getComputedStyle(el);
      const isInteractive = style.pointerEvents !== 'none';
      
      // For TD elements, ensure they have EXACTLY tabindex="0"
      if (el.tagName === 'TD') {
        const tabIndex = el.getAttribute('tabindex');
        const hasValidTabIndex = tabIndex === '0';
        
        // If tabindex is not exactly "0", exclude it
        if (!hasValidTabIndex) {
          return false;
        }
        
        // Additional TD-specific checks
        const isNotDisabledDate = !el.classList.contains('disabled') && 
                                 !el.classList.contains('is-other-month') &&
                                 !el.hasAttribute('disabled') &&
                                 el.getAttribute('aria-hidden') !== 'true';
        
        if (!isNotDisabledDate) {
          return false;
        }
      }
      

      return isVisible && isEnabled && isInteractive;
    });

    // Remove duplicates while preserving order (buttons first)
    const uniqueElements = filteredElements.filter((el, index, arr) => 
      arr.indexOf(el) === index
    );


    return uniqueElements;
  }

  private isElementDisabled(element: HTMLElement): boolean {
    if (!element) return true;
    
    // Check the element itself
    const isDisabled = element.classList.contains('disabled') ||
                      element.classList.contains('is-other-month') ||
                      element.hasAttribute('disabled') ||
                      element.getAttribute('aria-disabled') === 'true' ||
                      element.getAttribute('aria-hidden') === 'true' ||
                      element.getAttribute('tabindex') === '-1';
    
    if (isDisabled) {
      return true;
    }
    
    // Check if it's inside a disabled parent
    const disabledParent = element.closest('.disabled, [disabled], [aria-disabled="true"], [aria-hidden="true"]');
    if (disabledParent) {
      return true;
    }
    
    // Check if it contains disabled children (for TDs)
    if (element.tagName === 'TD') {
      const hasDisabledChild = element.querySelector('.disabled, [aria-hidden="true"], [tabindex="-1"]');
      if (hasDisabledChild) {
        return true;
      }
    }
    
    return false;
  }

  private redirectFocusToEnabled(): void {
    const calendar = document.querySelector('bs-datepicker-container');
    if (!calendar) return;
    
    const enabledElements = this.getFocusableElements(calendar);
    if (enabledElements.length > 0) {
      enabledElements[0].focus();
    } else {
      this.dp.hide();
      const toggleButton = this.elementRef.nativeElement.querySelector('.datepicker-toggle');
      if (toggleButton) {
        setTimeout(() => toggleButton.focus(), 50);
      }
    }
  }

  private focusFirstCalendarElement(calendar: Element) {
    
    // Priority order for initial focus - PRIORITIZE DATE CELLS over navigation buttons
    const selectors = [
      'td.selected:not(.disabled)',      // Selected date (highest priority)
      'td.today:not(.disabled)',         // Today's date  
      'td:not(.disabled):not(.is-other-month)', // First available date in current month
      'td:not(.disabled)',               // Any available date
      '.bs-datepicker-head button:not([disabled])'  // Navigation buttons (last resort)
    ];

    for (const selector of selectors) {
      const element = calendar.querySelector(selector) as HTMLElement;
      if (element && element.offsetParent !== null) {
        // Additional check to ensure we're not focusing header when body dates are available
        if (element.closest('.bs-datepicker-head')) {
          // This is a header button - check if body dates are available first
          const bodyDates = calendar.querySelectorAll('td:not(.disabled):not(.is-other-month)');
          if (bodyDates.length > 0) {
            continue;
          }
        }
        
        element.focus();        
        return;
      }
    }

    // Emergency fallback - try any focusable element
    const emergencyElements = this.getEmergencyFocusableElements(calendar);
    if (emergencyElements.length > 0) {
      emergencyElements[0].focus();
      return;
    }

    // If no elements found, close the calendar
    if (this.dp) {
      this.dp.hide();
    }
  }

  get toggleButtonLabel(): string {
    if (this.readOnly || this.field?.disabled) {
      return 'date-picker disabled';
    }
    return 'Choose date';
  }

  private onDatepickerShown() {
    setTimeout(() => {
      const calendar = document.querySelector('bs-datepicker-container');
      if (!calendar) return;

      // Set up ARIA attributes for accessibility
      this.renderer.setAttribute(calendar, 'role', 'dialog');
      this.renderer.setAttribute(calendar, 'aria-modal', 'true');
      this.renderer.setAttribute(calendar, 'aria-label', 'Date picker calendar');

      // Set up enabled navigation buttons only
      const enabledNavButtons = calendar.querySelectorAll('.bs-datepicker-head button:not([disabled])');
      enabledNavButtons.forEach((button: HTMLElement) => {
        this.renderer.setAttribute(button, 'tabindex', '0');
        
        // Use enhanced view detection method
        setTimeout(() => {
          // Give calendar time to fully render before detecting view
          this.updateNavigationButtonLabels(calendar);
        }, 50);

        // Add click handler to manage focus after header interactions AND refresh date announcements
        const clickHandler = this.renderer.listen(button, 'click', () => {
          // Small delay to allow ngx-bootstrap to update the calendar
          setTimeout(() => {
            // After header click, ensure the button retains focus for proper tab navigation
            if (document.activeElement !== button) {
              button.focus();
            }
            
            // Refresh date announcements after month/year change AND update button labels
            this.refreshDateAnnouncements();
            this.updateNavigationButtonLabels(calendar);
          }, 150); // Delay to ensure calendar has fully updated
        });
        
        this.focusTrapListeners.push(clickHandler);
      });

      // ALSO monitor ALL clickable elements in the header (month/year selectors, etc.)
      const allHeaderElements = calendar.querySelectorAll('.bs-datepicker-head *');
      allHeaderElements.forEach((element: HTMLElement) => {
        // Skip if it's already a button we handled above
        if (element.tagName === 'BUTTON') return;
        
        // Add click listener to any other interactive header elements
        if (element.onclick || element.style.cursor === 'pointer' || 
            element.classList.contains('current') || 
            element.getAttribute('role') === 'button' ||
            element.tabIndex >= 0) {
          
          const headerClickHandler = this.renderer.listen(element, 'click', () => {
            // Delay to allow calendar updates
            setTimeout(() => {
              this.refreshDateAnnouncements();
            }, 200); // Longer delay for dropdown/selector updates
          });
          
          this.focusTrapListeners.push(headerClickHandler);
        }
      });

      // Ensure disabled navigation buttons are not in tab order
      const disabledNavButtons = calendar.querySelectorAll('.bs-datepicker-head button[disabled]');
      disabledNavButtons.forEach((button: HTMLElement) => {
        this.renderer.setAttribute(button, 'tabindex', '-1');
        this.renderer.setAttribute(button, 'aria-hidden', 'true');
      });

      // Continuous monitoring for disabled header buttons (ngx-bootstrap may set disabled state later)
      const headerMonitor = setInterval(() => {
        // Update navigation button labels in case view has changed
        this.updateNavigationButtonLabels(calendar);
        
        const newDisabledButtons = calendar.querySelectorAll('.bs-datepicker-head button[disabled]');
        newDisabledButtons.forEach((button: HTMLElement) => {
          if (button.getAttribute('tabindex') !== '-1') {
            this.renderer.setAttribute(button, 'tabindex', '-1');
            this.renderer.setAttribute(button, 'aria-hidden', 'true');
          }
        });
      }, 50); // Increased frequency for faster label updates

      // Store monitor so we can clean it up
      this.focusTrapListeners.push(() => clearInterval(headerMonitor));
      
      // Additional specific monitoring for view changes (detect DOM changes)
      const viewChangeMonitor = setInterval(() => {
        // Force label update when view switches
        this.updateNavigationButtonLabels(calendar);
        // Also update year/month cell labels when view changes
        this.updateYearMonthCellLabels(calendar);
      }, 25); // Even more frequent for view transitions

      this.focusTrapListeners.push(() => clearInterval(viewChangeMonitor));

      // Continuous monitoring for other-month dates (ngx-bootstrap may add is-other-month class later)
      const otherMonthMonitor = setInterval(() => {
        const otherMonthCells = calendar.querySelectorAll('td.is-other-month');
        otherMonthCells.forEach((cell: HTMLElement) => {
          if (cell.getAttribute('tabindex') !== '-1') {
            this.renderer.setAttribute(cell, 'tabindex', '-1');
            this.renderer.setAttribute(cell, 'aria-hidden', 'true');
            this.renderer.setStyle(cell, 'pointer-events', 'none');
            
            // Also disable any child elements
            const childElements = cell.querySelectorAll('*');
            childElements.forEach((child: HTMLElement) => {
              this.renderer.setAttribute(child, 'tabindex', '-1');
              this.renderer.setAttribute(child, 'aria-hidden', 'true');
              this.renderer.setStyle(child, 'pointer-events', 'none');
            });
          }
        });
        
        // Also check for any TD with child spans that have is-other-month class
        const allTDs = calendar.querySelectorAll('td');
        allTDs.forEach((td: HTMLElement) => {
          const hasOtherMonthChild = td.querySelector('span.is-other-month, .is-other-month');
          if (hasOtherMonthChild && td.getAttribute('tabindex') !== '-1') {
            this.renderer.setAttribute(td, 'tabindex', '-1');
            this.renderer.setAttribute(td, 'aria-hidden', 'true');
            this.renderer.setStyle(td, 'pointer-events', 'none');
          }
        });
      }, 100);

      // Store monitor so we can clean it up
      this.focusTrapListeners.push(() => clearInterval(otherMonthMonitor));

      // Set up calendar cells with forced focusability and keyboard interaction
      const calendarCells = calendar.querySelectorAll('td');
      
      calendarCells.forEach((cell: HTMLElement) => {
        
        // Check if cell is disabled in any way
        const isDisabled = cell.classList.contains('disabled') || 
                          cell.classList.contains('is-other-month') || 
                          cell.hasAttribute('disabled') ||
                          cell.getAttribute('aria-disabled') === 'true';
        
        if (isDisabled) {
          // EXPLICITLY EXCLUDE disabled dates from tab navigation
          this.renderer.setAttribute(cell, 'tabindex', '-1');
          this.renderer.setAttribute(cell, 'aria-hidden', 'true');
          
          // CRITICAL: Also disable any child elements (spans, decorators, etc.)
          const childElements = cell.querySelectorAll('*');
          childElements.forEach((child: HTMLElement) => {
            this.renderer.setAttribute(child, 'tabindex', '-1');
            this.renderer.setAttribute(child, 'aria-hidden', 'true');
            this.renderer.setStyle(child, 'pointer-events', 'none');
            
            // Add aggressive event blocking on child elements
            this.renderer.listen(child, 'focus', (event: FocusEvent) => {
              event.preventDefault();
              event.stopImmediatePropagation();
              this.redirectFocusToEnabled();
            });
            
            this.renderer.listen(child, 'click', (event: MouseEvent) => {
              event.preventDefault();
              event.stopImmediatePropagation();
            });
          });
          
          // Block focus on the main disabled cell too
          this.renderer.listen(cell, 'focus', (event: FocusEvent) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.redirectFocusToEnabled();
          });
          
          // Remove any focus-related attributes that might make it focusable
          this.renderer.removeAttribute(cell, 'role');
          this.renderer.removeAttribute(cell, 'aria-label');
          this.renderer.setStyle(cell, 'pointer-events', 'none');
        } else {
          // Set up focusable cells for enabled, non-disabled dates
          this.renderer.setAttribute(cell, 'tabindex', '0');
          this.renderer.setAttribute(cell, 'role', 'button'); // Changed from gridcell to button for better selection semantics
          
          // Get current month and year for full date context with extensive debugging
          const monthYearText = calendar.querySelector('.current')?.textContent?.trim() || '';
          const dayText = cell.textContent?.trim() || '';
          
          
          // Try multiple selectors to find month and year information
          const monthButton = calendar.querySelector('button[aria-label*="month"], .bs-datepicker-head button:nth-child(2)');
          const yearButton = calendar.querySelector('button[aria-label*="year"], .bs-datepicker-head button:nth-child(3)');
          const headerText = calendar.querySelector('.bs-datepicker-head')?.textContent?.trim() || '';
          const headerButtons = Array.from(calendar.querySelectorAll('.bs-datepicker-head button'));
          
         
          
          
          // Create comprehensive aria-label with full date context
          let fullDateLabel = '';
          let month = '', year = '';
          
          // Strategy 1: Parse from main .current element if it has space-separated values
          if (monthYearText.includes(' ')) {
            const parts = monthYearText.split(' ').filter(part => part.trim());
            if (parts.length >= 2) {
              // Look for month (should be text) and year (should be number)
              const monthPart = parts.find(part => /^[a-zA-Z]+$/.test(part));
              const yearPart = parts.find(part => /^\d{4}$/.test(part));
              month = monthPart || parts[0];
              year = yearPart || parts[parts.length - 1];
            }
          }
          // Strategy 2: Parse concatenated format like "February2025"
          else if (monthYearText.match(/[a-zA-Z]+\d+/)) {
            const match = monthYearText.match(/([a-zA-Z]+)(\d+)/);
            if (match) {
              month = match[1];
              year = match[2];
            }
          }
          
          // Strategy 3: Look for separate month/year in header buttons
          if (!month || !year) {
            
            // Try header buttons in order
            if (headerButtons.length >= 2) {
              month = month || headerButtons[1]?.textContent?.trim() || '';
              year = year || headerButtons[2]?.textContent?.trim() || '';
            }
            
            // Try specific button selectors
            month = month || monthButton?.textContent?.trim() || '';
            year = year || yearButton?.textContent?.trim() || '';
            
            // Extract from overall header text as last resort
            if (!month || !year) {
              const headerMatch = headerText.match(/([a-zA-Z]+)\s*(\d{4})/);
              if (headerMatch) {
                month = month || headerMatch[1];
                year = year || headerMatch[2];
              }
            }
          }
          
          // Clean up extracted values
          month = month.replace(/[^\w]/g, ''); // Remove any non-word characters
          year = year.replace(/[^\d]/g, ''); // Keep only digits
          
          // Get the day of the week for this date
          let dayOfWeek = '';
          if (month && year && dayText) {
            const monthIndex = this.getMonthIndex(month);
            if (monthIndex !== -1) {
              const dateObj = new Date(parseInt(year), monthIndex, parseInt(dayText));
              const locale = this.translateService.currentLang === 'sp' ? 'es-US' : 'en-US';
              dayOfWeek = dateObj.toLocaleDateString(locale, { weekday: 'long' });
            }
          }
          
          // Create full date string with day of week
          if (month && year && dayText) {
            fullDateLabel = dayOfWeek 
              ? `${dayOfWeek}, ${month} ${dayText}, ${year}`
              : `${month} ${dayText}, ${year}`;
          } else if (monthYearText && dayText) {
            // Fallback: use whatever we got from monthYearText
            fullDateLabel = `${monthYearText} ${dayText}`;
          } else {
            // Final fallback to just day
            fullDateLabel = dayText;
          }
          
          
          // Add keyboard interaction hint for screen readers with full context
          let finalAriaLabel = '';
          if (cell.classList.contains('today')) {
            finalAriaLabel = `Today, ${fullDateLabel}. Press Enter or Space to select`;
          } else if (cell.classList.contains('selected')) {
            finalAriaLabel = `Selected date, ${fullDateLabel}. Press Enter or Space to select`;
          } else {
            finalAriaLabel = `${fullDateLabel}. Press Enter or Space to select`;
          }
          
          this.renderer.setAttribute(cell, 'aria-label', finalAriaLabel);
          
          // Force focus capability on TD elements
          if (!cell.getAttribute('onclick')) {
            this.renderer.listen(cell, 'focus', () => {
              // Ensure the cell is truly focused
              if (document.activeElement !== cell) {
                cell.focus();
              }
            });
            
            // Add keyboard activation listener directly to each cell
            this.renderer.listen(cell, 'keydown', (event: KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                
                // Try multiple activation methods
                
                // Method 1: Direct click
                cell.click();
                
                // Method 2: Find and click any child element that might be clickable
                setTimeout(() => {
                  const clickableChild = cell.querySelector('span, div, [data-date]');
                  if (clickableChild) {
                    (clickableChild as HTMLElement).click();
                  }
                  
                  // Method 3: Trigger custom date selection if available
                  const dataDate = cell.getAttribute('data-date') || cell.textContent?.trim();
                  if (dataDate) {
                    
                    // Create and dispatch a custom event
                    const customEvent = new CustomEvent('dateSelect', {
                      detail: { date: dataDate, element: cell },
                      bubbles: true
                    });
                    cell.dispatchEvent(customEvent);
                  }
                  
                  // Method 4: Try simulating mouse events
                  const mouseEvent = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    clientX: cell.getBoundingClientRect().left + cell.getBoundingClientRect().width / 2,
                    clientY: cell.getBoundingClientRect().top + cell.getBoundingClientRect().height / 2
                  });
                  cell.dispatchEvent(mouseEvent);
                  
                }, 10);
              }
            });
                       
          }
        }
      });

      // Set up year and month view cells for keyboard interaction
      const yearCells = calendar.querySelectorAll('.bs-years-calendar-view td:not(.disabled)');
      yearCells.forEach((cell: HTMLElement) => {
        if (!cell.classList.contains('disabled')) {
          this.renderer.setAttribute(cell, 'tabindex', '0');
          this.renderer.setAttribute(cell, 'role', 'button');
          
          // More aggressive approach to get only this cell's year
          let yearText = '';
          
          // Method 1: Try to get the span content first
          const spanElement = cell.querySelector('span');
          if (spanElement) {
            yearText = spanElement.textContent?.trim() || '';
          } 
          
          // Method 2: If no span, clone the cell and remove all neighboring content
          if (!yearText) {
            const cellClone = cell.cloneNode(true) as HTMLElement;
            // Remove any nested elements that might contain neighboring text
            const nestedElements = cellClone.querySelectorAll('*');
            nestedElements.forEach(el => {
              if (el.tagName !== 'SPAN') {
                el.remove();
              }
            });
            yearText = cellClone.textContent?.trim() || '';
          }
          
          // Method 3: Direct text node extraction as fallback
          if (!yearText) {
            const textNodes = Array.from(cell.childNodes)
              .filter(node => node.nodeType === Node.TEXT_NODE)
              .map(node => node.textContent?.trim())
              .filter(text => text && text.length > 0);
            yearText = textNodes.join('').trim();
          }
          
          // Method 4: Last resort - use innerHTML and strip HTML
          if (!yearText) {
            yearText = cell.innerHTML.replace(/<[^>]*>/g, '').trim();
          }
          
          // Extract only the 4-digit year if there's extra content
          const yearMatch = yearText.match(/\b(19|20)\d{2}\b/);
          if (yearMatch) {
            yearText = yearMatch[0];
          }
          
          // Additional safety: if we still have multiple years, take the first one
          const allYearMatches = yearText.match(/\b(19|20)\d{2}\b/g);
          if (allYearMatches && allYearMatches.length > 1) {
            yearText = allYearMatches[0];
          }
          
          // Clean approach: Use only aria-label to avoid duplicate announcements
          // ACCESSIBILITY FIX: Previously multiple aria attributes (aria-label, aria-describedby, 
          // hidden spans, title) were causing screen readers to announce year multiple times.
          // Now using single aria-label with original content hidden to ensure clean reading.
          // Remove any existing accessibility attributes that might cause duplication
          this.renderer.removeAttribute(cell, 'aria-describedby');
          this.renderer.removeAttribute(cell, 'aria-labelledby');
          this.renderer.removeAttribute(cell, 'title');
          
          // Remove any existing hidden elements that might cause duplication
          const existingHiddenSpan = cell.querySelector('.sr-only-year');
          if (existingHiddenSpan) {
            existingHiddenSpan.remove();
          }
          
          // Hide the original content from screen readers to prevent duplication
          const originalContent = cell.childNodes;
          originalContent.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.renderer.setAttribute(node as HTMLElement, 'aria-hidden', 'true');
            } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
              // Wrap text nodes in a span with aria-hidden to prevent duplication
              const wrapper = this.renderer.createElement('span');
              this.renderer.setAttribute(wrapper, 'aria-hidden', 'true');
              this.renderer.insertBefore(cell, wrapper, node);
              this.renderer.appendChild(wrapper, node);
            }
          });
          
          // Set only the aria-label with clean year text for screen readers
          this.renderer.setAttribute(cell, 'aria-label', yearText);
                    
          // Add keyboard activation
          this.renderer.listen(cell, 'keydown', (event: KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              cell.click();
            }
          });
        }
      });

      const monthCells = calendar.querySelectorAll('.bs-months-calendar-view td:not(.disabled)');
      
      // ACCESSIBILITY FIX: Aggressively suppress table navigation announcements for month view
      const monthTable = calendar.querySelector('.bs-months-calendar-view table');
      if (monthTable) {
        // Hide the entire table structure from screen readers
        this.renderer.setAttribute(monthTable, 'aria-hidden', 'true');
        this.renderer.setAttribute(monthTable, 'role', 'none');
        
        // Also hide all table structural elements
        const monthTableParts = monthTable.querySelectorAll('tbody, thead, tr, th');
        monthTableParts.forEach(part => {
          this.renderer.setAttribute(part as HTMLElement, 'aria-hidden', 'true');
          this.renderer.setAttribute(part as HTMLElement, 'role', 'none');
        });
      }
      
      monthCells.forEach((cell: HTMLElement) => {
        if (!cell.classList.contains('disabled')) {
          this.renderer.setAttribute(cell, 'tabindex', '0');
          
          // COMPLETE OVERRIDE: Set as standalone button element, completely removing table semantics
          this.renderer.setAttribute(cell, 'role', 'button');
          this.renderer.setAttribute(cell, 'aria-hidden', 'false'); // Explicitly make cell visible while table is hidden
          
          // Clean approach for month cells - remove any conflicting attributes
          this.renderer.removeAttribute(cell, 'aria-describedby');
          this.renderer.removeAttribute(cell, 'aria-labelledby');
          this.renderer.removeAttribute(cell, 'title');
          
          // Get the month text
          const monthText = cell.textContent?.trim() || '';
          
          // Get the year from the header with enhanced detection
          let yearText = '';
          
          // Method 1: Try to find year button in header (most common)
          const yearButtons = [
            calendar.querySelector('.bs-datepicker-head button[aria-label*="year"]'),
            calendar.querySelector('.bs-datepicker-head button:last-child'),
            calendar.querySelector('.bs-datepicker-head button:nth-child(3)'), // Often the year button
            calendar.querySelector('.bs-datepicker-head button:nth-child(2)'), // Sometimes year is 2nd
          ];
          
          for (const yearButton of yearButtons) {
            if (yearButton && !yearText) {
              const buttonText = yearButton.textContent?.trim() || '';
              const yearMatch = buttonText.match(/\b(19|20)\d{2}\b/);
              if (yearMatch) {
                yearText = yearMatch[0];
                break;
              }
            }
          }
          
          // Method 2: Look for year anywhere in header text
          if (!yearText) {
            const headerText = calendar.querySelector('.bs-datepicker-head')?.textContent?.trim() || '';
            const yearMatch = headerText.match(/\b(19|20)\d{2}\b/);
            if (yearMatch) {
              yearText = yearMatch[0];
            }
          }
          
          // Method 3: Look in current/active button or span
          if (!yearText) {
            const currentElements = [
              calendar.querySelector('.bs-datepicker-head .current'),
              calendar.querySelector('.bs-datepicker-head button[class*="current"]'),
              calendar.querySelector('.bs-datepicker-head span'),
              calendar.querySelector('.bs-datepicker-head .btn-secondary'),
            ];
            
            for (const element of currentElements) {
              if (element && !yearText) {
                const elementText = element.textContent?.trim() || '';
                const yearMatch = elementText.match(/\b(19|20)\d{2}\b/);
                if (yearMatch) {
                  yearText = yearMatch[0];
                  break;
                }
              }
            }
          }
          
          // Method 4: Parse from URL or data attributes if available
          if (!yearText) {
            const calendarContainer = calendar as HTMLElement;
            const dataYear = calendarContainer.getAttribute('data-year') || 
                            calendarContainer.getAttribute('year') ||
                            calendarContainer.querySelector('[data-year]')?.getAttribute('data-year');
            if (dataYear) {
              const yearMatch = dataYear.match(/\b(19|20)\d{2}\b/);
              if (yearMatch) {
                yearText = yearMatch[0];
              }
            }
          }
          
          // Method 5: Default to current year if nothing found
          if (!yearText) {
            yearText = new Date().getFullYear().toString();
          }
          
          // Create comprehensive aria-label with month and year
          const fullMonthLabel = `${monthText} ${yearText}`;
                    
          // Set aria-label for screen readers - this will override table semantics
          this.renderer.setAttribute(cell, 'aria-label', fullMonthLabel);
          
          // IMPORTANT: Add table accessibility attributes to suppress table navigation announcements
          // Remove table semantics that cause "row 1 column 1" announcements
          this.renderer.setAttribute(cell, 'role', 'button'); // Override table cell role
          this.renderer.setAttribute(cell, 'aria-roledescription', 'month selector'); // Override role description
          
          // Hide the original content from screen readers to prevent duplication
          const originalContent = Array.from(cell.childNodes);
          originalContent.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.renderer.setAttribute(node as HTMLElement, 'aria-hidden', 'true');
            } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
              // Wrap text nodes in a span with aria-hidden to prevent duplication
              const wrapper = this.renderer.createElement('span');
              this.renderer.setAttribute(wrapper, 'aria-hidden', 'true');
              this.renderer.insertBefore(cell, wrapper, node);
              this.renderer.appendChild(wrapper, node);
            }
          });
                    
          // Add keyboard activation
          this.renderer.listen(cell, 'keydown', (event: KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              cell.click();
            }
          });
        }
      });

      // FINAL CLEANUP: Aggressively scan for any missed disabled elements and remove them from tab order
      setTimeout(() => {
        
        // Find all disabled elements including child spans/decorators
        const allDisabledElements = calendar.querySelectorAll('.disabled, [disabled], [aria-disabled="true"], .is-other-month');
        allDisabledElements.forEach((element: HTMLElement) => {
          this.renderer.setAttribute(element, 'tabindex', '-1');
          this.renderer.setAttribute(element, 'aria-hidden', 'true');
          this.renderer.setStyle(element, 'pointer-events', 'none');
          
          // Also disable any children of disabled elements
          const children = element.querySelectorAll('*');
          children.forEach((child: HTMLElement) => {
            this.renderer.setAttribute(child, 'tabindex', '-1');
            this.renderer.setAttribute(child, 'aria-hidden', 'true');
            this.renderer.setStyle(child, 'pointer-events', 'none');
          });
        });
        
        // CRITICAL: Specifically target bsdatepickerdaydecorator spans that are disabled
        const disabledSpans = calendar.querySelectorAll('span.disabled, span[bsdatepickerdaydecorator].disabled');
        disabledSpans.forEach((span: HTMLElement) => {
          this.renderer.setAttribute(span, 'tabindex', '-1');
          this.renderer.setAttribute(span, 'aria-hidden', 'true');
          this.renderer.setStyle(span, 'pointer-events', 'none');
        });
        
        // Double-check: Find any elements with tabindex="0" that might still be disabled
        const tabindexElements = calendar.querySelectorAll('[tabindex="0"]');
        tabindexElements.forEach((element: HTMLElement) => {
          const isDisabled = element.classList.contains('disabled') || 
                            element.classList.contains('is-other-month') || 
                            element.hasAttribute('disabled') ||
                            element.getAttribute('aria-disabled') === 'true';
          
          if (isDisabled) {
            this.renderer.setAttribute(element, 'tabindex', '-1');
            this.renderer.setAttribute(element, 'aria-hidden', 'true');
            this.renderer.setStyle(element, 'pointer-events', 'none');
          }
        });
        
        
        // EMERGENCY OVERRIDE: Add global focus interceptor for any disabled elements
        const focusInterceptor = this.renderer.listen('document', 'focusin', (event: FocusEvent) => {
          if (!this.dp?.isOpen) return;
          
          const target = event.target as HTMLElement;
          const calendar = document.querySelector('bs-datepicker-container');
          
          // If focus is on a disabled element within the calendar, redirect it
          if (calendar?.contains(target)) {
            const isDisabledElement = target.classList.contains('disabled') ||
                                    target.classList.contains('is-other-month') ||
                                    target.closest('.disabled') ||
                                    target.closest('.is-other-month') ||
                                    target.getAttribute('aria-hidden') === 'true' ||
                                    target.getAttribute('tabindex') === '-1';
            
            if (isDisabledElement) {
              event.preventDefault();
              event.stopImmediatePropagation();
              
              // Find first enabled element and focus it instead
              const enabledElements = this.getFocusableElements(calendar);
              if (enabledElements.length > 0) {
                setTimeout(() => enabledElements[0].focus(), 10);
              }
            }
          }
        });
        
        // Store the interceptor so we can clean it up later
        this.focusTrapListeners.push(focusInterceptor);
        
        // Comprehensive keyboard override with focus prevention and custom tab handling
        const keydownOverride = this.renderer.listen(calendar, 'keydown', (event: KeyboardEvent) => {
          const target = event.target as HTMLElement;
          
          if (event.key === 'Tab') {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.handleTabInCalendar(event);
            return;
          }
          
          // Block disabled element interaction completely
          if (this.isElementDisabled(target)) {
            if (['Enter', ' ', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
              event.preventDefault();
              event.stopImmediatePropagation();
              this.redirectFocusToEnabled();
            }
          }
        });
        
        this.focusTrapListeners.push(keydownOverride);
      }, 100);

      // Focus the first appropriate element
      this.focusFirstCalendarElement(calendar);
      
      // Lightweight backup system: monitor for header text changes
      let lastHeaderText = calendar.querySelector('.current')?.textContent?.trim() || '';
      
      const calendarChangeMonitor = setInterval(() => {
        if (!this.dp || !this.dp.isOpen) {
          clearInterval(calendarChangeMonitor);
          return;
        }
        
        const currentHeaderText = calendar.querySelector('.current')?.textContent?.trim() || '';
        if (currentHeaderText !== lastHeaderText && currentHeaderText) {
          lastHeaderText = currentHeaderText;
          this.refreshDateAnnouncements();
        }
      }, 500);
      
      // Clean up interval when calendar closes
      this.focusTrapListeners.push(() => clearInterval(calendarChangeMonitor));
    }, 100);
  }

  private onDatepickerHidden() {
    const inputField = this.elementRef.nativeElement.querySelector('input[bsDatepicker]');
    const toggleButton = this.elementRef.nativeElement.querySelector('.datepicker-toggle');
    
    // Announce the selected date value if available
    if (this.field?.value && toggleButton) {
      const selectedDate = this.field.value;
      let dateString = '';
      
      // Format the date for announcement
      if (selectedDate instanceof Date) {
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        const locale = this.translateService.currentLang === 'sp' ? 'es-US' : 'en-US';
        dateString = selectedDate.toLocaleDateString(locale, options);
      } else if (typeof selectedDate === 'string') {
        dateString = selectedDate;
      }
      
      // Temporarily modify the toggle button's aria-label to announce the selected date
      if (dateString) {
        this.renderer.setAttribute(toggleButton, 'aria-label', `Selected date: ${dateString}`);
        
        setTimeout(() => {
          // Reset aria-label back to normal
          this.renderer.setAttribute(toggleButton, 'aria-label', 'Choose date');
          
          // Move focus to the next element in natural tab order
          this.moveFocusToNextInTabOrder(inputField || toggleButton);
        }, 100);
      } else {
        // No date selected, return focus to input or toggle
        setTimeout(() => {
          if (inputField) {
            inputField.focus();
          } else {
            toggleButton.focus();
          }
        }, 50);
      }
    } else if (inputField) {
      setTimeout(() => {
        inputField.focus();
      }, 50);
    } else if (toggleButton) {
      setTimeout(() => {
        toggleButton.focus();
      }, 50);
    }
  }

  /**
   * Move focus to the next element in natural tab order (respects tabindex)
   */
  private moveFocusToNextInTabOrder(currentElement: HTMLElement) {
    try {
      const toggleButton = this.elementRef.nativeElement.querySelector('.datepicker-toggle');
      const inputField = this.elementRef.nativeElement.querySelector('input[bsDatepicker]');
      
      // Get all tabbable elements
      const tabbableSelector = [
        'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
        'select:not([disabled]):not([tabindex="-1"])',
        'textarea:not([disabled]):not([tabindex="-1"])',
        'button:not([disabled]):not([tabindex="-1"])',
        'a[href]:not([tabindex="-1"])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(', ');
      
      const allTabbable = Array.from(document.querySelectorAll(tabbableSelector)) as HTMLElement[];
      
      // Filter to visible elements only and exclude datepicker elements
      const visibleTabbable = allTabbable.filter(el => {
        if (el === toggleButton || el === inputField) {
          return false;
        }
        
        const rect = el.getBoundingClientRect();
        const style = getComputedStyle(el);
        return rect.width > 0 && 
               rect.height > 0 && 
               style.visibility !== 'hidden' && 
               style.display !== 'none';
      });
      
      // Sort by tabindex and DOM position
      const sortedTabbable = visibleTabbable.sort((a, b) => {
        const aIndex = parseInt(a.getAttribute('tabindex') || '0');
        const bIndex = parseInt(b.getAttribute('tabindex') || '0');
        
        // Elements with explicit positive tabindex come first
        if (aIndex > 0 && bIndex > 0) return aIndex - bIndex;
        if (aIndex > 0) return -1;
        if (bIndex > 0) return 1;
        
        // For elements with tabindex 0 or implicit, use DOM order
        const position = a.compareDocumentPosition(b);
        if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });
      
      // Find current element in sorted list
      const currentIndex = sortedTabbable.findIndex(el => 
        el === currentElement || el === inputField || el === toggleButton
      );
      
      // Get next element
      if (currentIndex !== -1 && currentIndex + 1 < sortedTabbable.length) {
        sortedTabbable[currentIndex + 1].focus();
      } else if (sortedTabbable.length > 0) {
        // If current not found or at end, focus first element after current in DOM
        const nextInDom = sortedTabbable.find(el => {
          const position = currentElement.compareDocumentPosition(el);
          return position & Node.DOCUMENT_POSITION_FOLLOWING;
        });
        
        if (nextInDom) {
          nextInDom.focus();
        }
      }
    } catch (error) {
      console.error('Error moving focus to next element:', error);
    }
  }

  private refreshDateAnnouncements() {
    const calendar = document.querySelector('bs-datepicker-container');
    if (!calendar) return;
    
    // Prevent multiple simultaneous refreshes
    if ((this as any).isRefreshing) return;
    (this as any).isRefreshing = true;
    
    try {
      // Detect what view we're in
      const isYearView = calendar.querySelector('.bs-years-calendar-view') !== null ||
                        calendar.querySelector('.bs-years-view') !== null ||
                        calendar.querySelector('[class*="years"]') !== null;
      
      const isMonthView = calendar.querySelector('.bs-months-calendar-view') !== null ||
                         calendar.querySelector('.bs-months-view') !== null ||
                         calendar.querySelector('[class*="months"]') !== null;
      
      if (isYearView) {
        // Handle year view cells
        const yearCells = calendar.querySelectorAll('.bs-years-calendar-view td:not(.disabled)') as NodeListOf<HTMLElement>;
        yearCells.forEach((cell: HTMLElement) => {
          if (!cell.classList.contains('disabled')) {
            this.updateYearCellAriaLabel(cell);
          }
        });
      } else if (isMonthView) {
        // Handle month view cells
        // ACCESSIBILITY FIX: Aggressively suppress table navigation announcements for month view
        const monthTable = calendar.querySelector('.bs-months-calendar-view table');
        if (monthTable) {
          this.renderer.setAttribute(monthTable, 'aria-hidden', 'true');
          this.renderer.setAttribute(monthTable, 'role', 'none');
          
          const monthTableParts = monthTable.querySelectorAll('tbody, thead, tr, th');
          monthTableParts.forEach(part => {
            this.renderer.setAttribute(part as HTMLElement, 'aria-hidden', 'true');
            this.renderer.setAttribute(part as HTMLElement, 'role', 'none');
          });
        }
        
        const monthCells = calendar.querySelectorAll('.bs-months-calendar-view td:not(.disabled)') as NodeListOf<HTMLElement>;
        monthCells.forEach((cell: HTMLElement) => {
          if (!cell.classList.contains('disabled')) {
            // Make cell visible while table structure is hidden
            this.renderer.setAttribute(cell, 'aria-hidden', 'false');
            this.updateMonthCellAriaLabel(cell);
          }
        });
      } else {
        // Handle regular date view cells
        const selectors = [
          'td[role="button"]',
          'td[tabindex="0"]', 
          'td.bs-datepicker-body-date',
          'td:not(.disabled):not(.is-other-month)',
          '.bs-calendar-body td'
        ];
        
        let dateCells: NodeListOf<HTMLElement> | null = null;
        
        for (const selector of selectors) {
          const cells = calendar.querySelectorAll(selector) as NodeListOf<HTMLElement>;
          if (cells.length > 0) {
            dateCells = cells;
            break;
          }
        }
        
        if (dateCells) {
          dateCells.forEach((cell: HTMLElement) => {
            if (!cell.classList.contains('disabled') && !cell.classList.contains('is-other-month')) {
              this.updateDateCellAriaLabel(cell, calendar);
            }
          });
        }
      }
    } catch (error) {
      console.error('Error during date announcements refresh:', error);
    } finally {
      setTimeout(() => {
        (this as any).isRefreshing = false;
      }, 100);
    }
  }

  private updateYearCellAriaLabel(cell: HTMLElement) {
    // Get the year text using the same logic as in onDatepickerShown
    let yearText = '';
    
    // Method 1: Try to get the span content first
    const spanElement = cell.querySelector('span');
    if (spanElement) {
      yearText = spanElement.textContent?.trim() || '';
    } 
    
    // Method 2: If no span, try direct text content
    if (!yearText) {
      yearText = cell.textContent?.trim() || '';
    }
    
    // Extract only the 4-digit year if there's extra content
    const yearMatch = yearText.match(/\b(19|20)\d{2}\b/);
    if (yearMatch) {
      yearText = yearMatch[0];
    }
    
    // Additional safety: if we still have multiple years, take the first one
    const allYearMatches = yearText.match(/\b(19|20)\d{2}\b/g);
    if (allYearMatches && allYearMatches.length > 1) {
      yearText = allYearMatches[0];
    }
    
    if (yearText) {
      // Clean approach: Use only aria-label to avoid duplicate announcements
      this.renderer.removeAttribute(cell, 'aria-describedby');
      this.renderer.removeAttribute(cell, 'aria-labelledby');
      this.renderer.removeAttribute(cell, 'title');
      
      // Remove any existing hidden elements that might cause duplication
      const existingHiddenSpan = cell.querySelector('.sr-only-year');
      if (existingHiddenSpan) {
        existingHiddenSpan.remove();
      }
      
      // Hide the original content from screen readers to prevent duplication
      const originalContent = Array.from(cell.childNodes);
      originalContent.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          this.renderer.setAttribute(node as HTMLElement, 'aria-hidden', 'true');
        } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
          // Wrap text nodes in a span with aria-hidden to prevent duplication
          const wrapper = this.renderer.createElement('span');
          this.renderer.setAttribute(wrapper, 'aria-hidden', 'true');
          this.renderer.insertBefore(cell, wrapper, node);
          this.renderer.appendChild(wrapper, node);
        }
      });
      
      // Set only the aria-label with clean year text for screen readers
      this.renderer.setAttribute(cell, 'aria-label', yearText);
    }
  }

  private updateMonthCellAriaLabel(cell: HTMLElement) {
    const monthText = cell.textContent?.trim() || '';
    if (monthText) {
      // COMPLETE OVERRIDE: Set as standalone button, removing table semantics
      this.renderer.setAttribute(cell, 'role', 'button');
      this.renderer.setAttribute(cell, 'aria-hidden', 'false'); // Explicitly make cell accessible
      this.renderer.setAttribute(cell, 'aria-roledescription', 'month selector'); // Override role description
      
      // Clean approach for month cells - remove any conflicting attributes
      this.renderer.removeAttribute(cell, 'aria-describedby');
      this.renderer.removeAttribute(cell, 'aria-labelledby');
      this.renderer.removeAttribute(cell, 'title');
      
      // Get the calendar container to find year information with enhanced detection
      const calendar = cell.closest('bs-datepicker-container');
      let yearText = '';
      
      if (calendar) {
        // Method 1: Try to find year button in header (multiple selectors)
        const yearButtons = [
          calendar.querySelector('.bs-datepicker-head button[aria-label*="year"]'),
          calendar.querySelector('.bs-datepicker-head button:last-child'),
          calendar.querySelector('.bs-datepicker-head button:nth-child(3)'),
          calendar.querySelector('.bs-datepicker-head button:nth-child(2)'),
        ];
        
        for (const yearButton of yearButtons) {
          if (yearButton && !yearText) {
            const buttonText = yearButton.textContent?.trim() || '';
            const yearMatch = buttonText.match(/\b(19|20)\d{2}\b/);
            if (yearMatch) {
              yearText = yearMatch[0];
              break;
            }
          }
        }
        
        // Method 2: Look for year anywhere in header text
        if (!yearText) {
          const headerText = calendar.querySelector('.bs-datepicker-head')?.textContent?.trim() || '';
          const yearMatch = headerText.match(/\b(19|20)\d{2}\b/);
          if (yearMatch) {
            yearText = yearMatch[0];
          }
        }
        
        // Method 3: Look in current/active elements
        if (!yearText) {
          const currentElements = [
            calendar.querySelector('.bs-datepicker-head .current'),
            calendar.querySelector('.bs-datepicker-head button[class*="current"]'),
            calendar.querySelector('.bs-datepicker-head span'),
            calendar.querySelector('.bs-datepicker-head .btn-secondary'),
          ];
          
          for (const element of currentElements) {
            if (element && !yearText) {
              const elementText = element.textContent?.trim() || '';
              const yearMatch = elementText.match(/\b(19|20)\d{2}\b/);
              if (yearMatch) {
                yearText = yearMatch[0];
                break;
              }
            }
          }
        }
        
        // Method 4: Default to current year if nothing found
        if (!yearText) {
          yearText = new Date().getFullYear().toString();
        }
      }
      
      // Create comprehensive aria-label with month and year
      const fullMonthLabel = `${monthText} ${yearText}`;
      
      // Set aria-label for screen readers - this will override table semantics
      this.renderer.setAttribute(cell, 'aria-label', fullMonthLabel);
      
      // Hide the original content from screen readers to prevent duplication
      const originalContent = Array.from(cell.childNodes);
      originalContent.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          this.renderer.setAttribute(node as HTMLElement, 'aria-hidden', 'true');
        } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
          // Wrap text nodes in a span with aria-hidden to prevent duplication
          const wrapper = this.renderer.createElement('span');
          this.renderer.setAttribute(wrapper, 'aria-hidden', 'true');
          this.renderer.insertBefore(cell, wrapper, node);
          this.renderer.appendChild(wrapper, node);
        }
      });      
    }
  }

  private updateDateCellAriaLabel(cell: HTMLElement, calendar: Element) {
    const dayText = cell.textContent?.trim() || '';
    const monthYearText = calendar.querySelector('.current')?.textContent?.trim() || '';
    
    // Extract month and year using multiple strategies
    let month = '', year = '';
    
    // Strategy 1: Parse space-separated values like "February 2025"
    if (monthYearText.includes(' ')) {
      const parts = monthYearText.split(' ').filter(part => part.trim());
      if (parts.length >= 2) {
        const monthPart = parts.find(part => /^[a-zA-Z]+$/.test(part));
        const yearPart = parts.find(part => /^\d{4}$/.test(part));
        month = monthPart || parts[0];
        year = yearPart || parts[parts.length - 1];
      }
    }
    // Strategy 2: Parse concatenated format like "February2025"
    else if (monthYearText.match(/[a-zA-Z]+\d+/)) {
      const match = monthYearText.match(/([a-zA-Z]+)(\d+)/);
      if (match) {
        month = match[1];
        year = match[2];
      }
    }
    
    // Strategy 3: Look for separate month/year elements if needed
    if (!month || !year) {
      const headerButtons = Array.from(calendar.querySelectorAll('.bs-datepicker-head button'));
      if (headerButtons.length >= 2) {
        month = month || headerButtons[1]?.textContent?.trim() || '';
        year = year || headerButtons[2]?.textContent?.trim() || '';
      }
      
      // Extract from overall header text as fallback
      if (!month || !year) {
        const headerText = calendar.querySelector('.bs-datepicker-head')?.textContent?.trim() || '';
        const headerMatch = headerText.match(/([a-zA-Z]+)\s*(\d{4})/);
        if (headerMatch) {
          month = month || headerMatch[1];
          year = year || headerMatch[2];
        }
      }
    }
    
    // Clean extracted values
    month = month.replace(/[^\w]/g, '');
    year = year.replace(/[^\d]/g, '');
    
    // Get the day of the week for this date
    let dayOfWeek = '';
    if (month && year && dayText) {
      const monthIndex = this.getMonthIndex(month);
      if (monthIndex !== -1) {
        const dateObj = new Date(parseInt(year), monthIndex, parseInt(dayText));
        const locale = this.translateService.currentLang === 'sp' ? 'es-US' : 'en-US';
        dayOfWeek = dateObj.toLocaleDateString(locale, { weekday: 'long' });
      }
    }
    
    // Create full date string with day of week
    let fullDateLabel = '';
    if (month && year && dayText) {
      fullDateLabel = dayOfWeek 
        ? `${dayOfWeek}, ${month} ${dayText}, ${year}`
        : `${month} ${dayText}, ${year}`;
    } else if (monthYearText && dayText) {
      fullDateLabel = `${monthYearText} ${dayText}`;
    } else {
      fullDateLabel = dayText;
    }
    
    // Create final aria-label with context
    let finalAriaLabel = '';
    if (cell.classList.contains('today')) {
      finalAriaLabel = `Today, ${fullDateLabel}. Press Enter or Space to select`;
    } else if (cell.classList.contains('selected')) {
      finalAriaLabel = `Selected date, ${fullDateLabel}. Press Enter or Space to select`;
    } else {
      finalAriaLabel = `${fullDateLabel}. Press Enter or Space to select`;
    }
    
    this.renderer.setAttribute(cell, 'aria-label', finalAriaLabel);
  }

  private getMonthIndex(monthName: string): number {
    if (!monthName) return -1;
    
    const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
                       'july', 'august', 'september', 'october', 'november', 'december'];
    const monthNamesEs = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                         'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    
    const monthLower = monthName.toLowerCase();
    let monthIndex = monthNames.indexOf(monthLower);
    if (monthIndex === -1) {
      monthIndex = monthNamesEs.indexOf(monthLower);
    }
    
    return monthIndex;
  }

  private updateNavigationButtonLabels(calendar: Element): void {
    const navButtons = calendar.querySelectorAll('.bs-datepicker-head button:not([disabled])');
    
    // Enhanced view mode detection with multiple fallback methods
    const isMonthView = calendar.querySelector('.bs-months-calendar-view') !== null ||
                       calendar.querySelector('.bs-months-view') !== null ||
                       calendar.querySelector('[class*="months"]') !== null;
    
    const isYearView = calendar.querySelector('.bs-years-calendar-view') !== null ||
                      calendar.querySelector('.bs-years-view') !== null ||
                      calendar.querySelector('[class*="years"]') !== null;
    
    // Additional detection by checking if we see year cells vs month cells vs date cells
    const hasYearCells = calendar.querySelectorAll('td').length > 0 && 
                        Array.from(calendar.querySelectorAll('td')).some(td => 
                          /^\d{4}$/.test(td.textContent?.trim() || ''));
    
    const hasMonthCells = calendar.querySelectorAll('td').length > 0 &&
                         Array.from(calendar.querySelectorAll('td')).some(td => {
                           const text = td.textContent?.trim() || '';
                           return /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/.test(text) ||
                                  /^(Ene|Feb|Mar|Abr|May|Jun|Jul|Ago|Sep|Oct|Nov|Dic)/.test(text);
                         });
    
    // Additional detection: Check for year range pattern like "2020-2029"
    const headerText = calendar.querySelector('.bs-datepicker-head')?.textContent?.trim() || '';
    const hasYearRange = /\d{4}\s*-\s*\d{4}/.test(headerText);
    
    // Additional detection: Check if the current button text contains years
    const currentButton = calendar.querySelector('.bs-datepicker-head .current, .bs-datepicker-head button[class*="current"]');
    const currentButtonHasYear = currentButton && /\d{4}/.test(currentButton.textContent?.trim() || '');
    
    // Final view determination with all fallbacks
    const finalIsYearView = isYearView || hasYearCells || hasYearRange;
    const finalIsMonthView = (isMonthView || hasMonthCells || currentButtonHasYear) && !finalIsYearView;
    
    
    navButtons.forEach((button: HTMLElement) => {      
      // Update aria-labels based on current view mode
      if (button.innerHTML.includes('‹')) {
        let newLabel = 'Previous month'; // default
        
        if (finalIsYearView) {
          newLabel = 'Previous year';
        } else if (finalIsMonthView) {
          newLabel = 'Previous year';
        } else {
          newLabel = 'Previous month';
        }
        
        // Force update even if it seems the same
        this.renderer.setAttribute(button, 'aria-label', newLabel);
        
      } else if (button.innerHTML.includes('›')) {
        let newLabel = 'Next month'; // default
        
        if (finalIsYearView) {
          newLabel = 'Next year';
        } else if (finalIsMonthView) {
          newLabel = 'Next year';
        } else {
          newLabel = 'Next month';
        }
        
        // Force update even if it seems the same
        this.renderer.setAttribute(button, 'aria-label', newLabel);
      }
    });
  }

  private updateYearMonthCellLabels(calendar: Element): void {
    // Update year cells
    const yearCells = calendar.querySelectorAll('.bs-years-calendar-view td:not(.disabled)');
    yearCells.forEach((cell: HTMLElement) => {
      if (!cell.classList.contains('disabled')) {
        // Ensure proper accessibility setup for year cells
        this.renderer.setAttribute(cell, 'tabindex', '0');
        this.renderer.setAttribute(cell, 'role', 'button');
        
        // More aggressive approach to get only this cell's year
        let yearText = '';
        
        // Method 1: Try to get the span content first
        const spanElement = cell.querySelector('span');
        if (spanElement) {
          yearText = spanElement.textContent?.trim() || '';
        } 
        
        // Method 2: If no span, clone the cell and remove all neighboring content
        if (!yearText) {
          const cellClone = cell.cloneNode(true) as HTMLElement;
          // Remove any nested elements that might contain neighboring text
          const nestedElements = cellClone.querySelectorAll('*');
          nestedElements.forEach(el => {
            if (el.tagName !== 'SPAN') {
              el.remove();
            }
          });
          yearText = cellClone.textContent?.trim() || '';
        }
        
        // Method 3: Direct text node extraction as fallback
        if (!yearText) {
          const textNodes = Array.from(cell.childNodes)
            .filter(node => node.nodeType === Node.TEXT_NODE)
            .map(node => node.textContent?.trim())
            .filter(text => text && text.length > 0);
          yearText = textNodes.join('').trim();
        }
        
        // Method 4: Last resort - use innerHTML and strip HTML
        if (!yearText) {
          yearText = cell.innerHTML.replace(/<[^>]*>/g, '').trim();
        }
        
        // Extract only the 4-digit year if there's extra content
        const yearMatch = yearText.match(/\b(19|20)\d{2}\b/);
        if (yearMatch) {
          yearText = yearMatch[0];
        }
        
        // Additional safety: if we still have multiple years, take the first one
        const allYearMatches = yearText.match(/\b(19|20)\d{2}\b/g);
        if (allYearMatches && allYearMatches.length > 1) {
          yearText = allYearMatches[0];
        }
        
        // Clean approach: Use only aria-label to avoid duplicate announcements
        // ACCESSIBILITY FIX: Prevents screen reader from announcing year multiple times
        // Remove any existing accessibility attributes that might cause duplication
        this.renderer.removeAttribute(cell, 'aria-describedby');
        this.renderer.removeAttribute(cell, 'aria-labelledby');
        this.renderer.removeAttribute(cell, 'title');
        
        // Remove any existing hidden elements that might cause duplication
        const existingHiddenSpan = cell.querySelector('.sr-only-year');
        if (existingHiddenSpan) {
          existingHiddenSpan.remove();
        }
        
        // Hide the original content from screen readers to prevent duplication
        const originalContent = Array.from(cell.childNodes);
        originalContent.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.renderer.setAttribute(node as HTMLElement, 'aria-hidden', 'true');
          } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
            // Wrap text nodes in a span with aria-hidden to prevent duplication
            const wrapper = this.renderer.createElement('span');
            this.renderer.setAttribute(wrapper, 'aria-hidden', 'true');
            this.renderer.insertBefore(cell, wrapper, node);
            this.renderer.appendChild(wrapper, node);
          }
        });
        
        // Set only the aria-label with clean year text for screen readers
        this.renderer.setAttribute(cell, 'aria-label', yearText);
      }
    });

    // Update month cells
    // ACCESSIBILITY FIX: Aggressively suppress table navigation announcements for month view
    const monthTable = calendar.querySelector('.bs-months-calendar-view table');
    if (monthTable) {
      this.renderer.setAttribute(monthTable, 'aria-hidden', 'true');
      this.renderer.setAttribute(monthTable, 'role', 'none');
      
      const monthTableParts = monthTable.querySelectorAll('tbody, thead, tr, th');
      monthTableParts.forEach(part => {
        this.renderer.setAttribute(part as HTMLElement, 'aria-hidden', 'true');
        this.renderer.setAttribute(part as HTMLElement, 'role', 'none');
      });
    }
    
    const monthCells = calendar.querySelectorAll('.bs-months-calendar-view td:not(.disabled)');
    monthCells.forEach((cell: HTMLElement) => {
      if (!cell.classList.contains('disabled')) {
        // Ensure proper accessibility setup for month cells
        this.renderer.setAttribute(cell, 'tabindex', '0');
        this.renderer.setAttribute(cell, 'role', 'button');
        this.renderer.setAttribute(cell, 'aria-hidden', 'false'); // Make cell accessible while table is hidden
        this.renderer.setAttribute(cell, 'aria-roledescription', 'month selector'); // Override role description
        
        // Clean approach for month cells - remove any conflicting attributes
        this.renderer.removeAttribute(cell, 'aria-describedby');
        this.renderer.removeAttribute(cell, 'aria-labelledby');
        this.renderer.removeAttribute(cell, 'title');
        
        // Get the month text
        const monthText = cell.textContent?.trim() || '';
        
        // Get the year from the header
        let yearText = '';
        
        // Method 1: Try to find year button in header
        const yearButton = calendar.querySelector('.bs-datepicker-head button[aria-label*="year"], .bs-datepicker-head button:last-child');
        if (yearButton) {
          yearText = yearButton.textContent?.trim() || '';
        }
        
        // Method 2: Look for year in header text if button method fails
        if (!yearText) {
          const headerText = calendar.querySelector('.bs-datepicker-head')?.textContent?.trim() || '';
          const yearMatch = headerText.match(/\b(19|20)\d{2}\b/);
          if (yearMatch) {
            yearText = yearMatch[0];
          }
        }
        
        // Method 3: Look in current button or any text containing year
        if (!yearText) {
          const currentButton = calendar.querySelector('.bs-datepicker-head .current, .bs-datepicker-head button[class*="current"]');
          if (currentButton) {
            const currentText = currentButton.textContent?.trim() || '';
            const yearMatch = currentText.match(/\b(19|20)\d{2}\b/);
            if (yearMatch) {
              yearText = yearMatch[0];
            }
          }
        }
        
        // Create comprehensive aria-label with month and year
        let fullMonthLabel = monthText;
        if (yearText) {
          fullMonthLabel = `${monthText} ${yearText}`;
        }
        
        // Set aria-label for screen readers - this will override table semantics
        this.renderer.setAttribute(cell, 'aria-label', fullMonthLabel);
        
        // Hide the original content from screen readers to prevent duplication  
        const originalContent = Array.from(cell.childNodes);
        originalContent.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.renderer.setAttribute(node as HTMLElement, 'aria-hidden', 'true');
          } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
            // Wrap text nodes in a span with aria-hidden to prevent duplication
            const wrapper = this.renderer.createElement('span');
            this.renderer.setAttribute(wrapper, 'aria-hidden', 'true');
            this.renderer.insertBefore(cell, wrapper, node);
            this.renderer.appendChild(wrapper, node);
          }
        });        
      }
    });
  }

  private languageCheck(language: string) {
    if (language === 'sp') {
      this.localeService?.use('es');
    } else {
      this.localeService?.use('en');
    }
  }
}